import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CheckboxModule } from 'primeng/checkbox';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { debounceTime, distinctUntilChanged, merge } from 'rxjs';
import {
  buildEmptyValueFilter,
  buildFilterOptionsWithEmpty,
  FilterOption,
  matchesEmptyValueSelection,
} from '../../../../services/filter-options.service';
import { PAGINATION_CONSTANTS } from '../../../../constants/pagination.constants';
import { buildLocationFilterOptions, matchesLocationFilter } from '../../../../utils/location-filter';
import { LazyLoadConverter } from '../../../../utils/lazy-load.converter';
import { formatUnitLabel } from '../../../../utils/unit-select.utils';
import { SelectOption } from '../../../../types/entity-definition.interface';
import { MeasureDataService } from '../../../../services/measure-data.service';
import { ToastService } from '../../../../services/toast.service';
import { LocationStore } from '../../../../stores/location.store';
import { MeasureLocationStore } from '../../../../stores/measure-location.store';
import { MeasureTypeStore } from '../../../../stores/measure-type.store';
import { UnitStore } from '../../../../stores/unit.store';
import { GraphMeasure, toGraphMeasure } from '../../../../types/graph-data.interface';
import { MeasureSelectionResult } from '../../../../types/graph-dialog.interface';
import { getGenreLabelKey, MeasureType } from '../../../../types/table.interface';
import { FilterParams, LazyLoadParams } from '../../../../types/lazy-load.interface';
import { Location } from '../../../../types/location.interface';
import { MeasureLocation } from '../../../../types/measure-location.interface';
import { MeasureType as MeasureTypeEntity } from '../../../../types/measure-type.interface';
import { Unit } from '../../../../types/unit.interface';
import { CesDialogFooterComponent } from '../../dialog-footer/dialog-footer.component';
import { CesIconComponent } from '../../ces-icon/ces-icon.component';
import { SuspendedMeasureIconComponent } from '../../suspended-measure-icon/suspended-measure-icon.component';

interface SelectMeasuresDialogData {
  assetId: number;
  preselectedIds?: number[];
  multiselect?: boolean;
}

const SORT_COLLATOR = new Intl.Collator();

// Sort keys are the server-side field names; the extractors mirror the server
// sort expressions on the client-side rows of the selected-only view: unit
// sorts on the displayed symbol, genre on the stable numeric type, and
// multi-location rows on their first location (best effort — the aggregated
// label cannot distinguish a ', ' separator from one inside a location name).
const CLIENT_SORT_VALUES: Record<string, (measure: GraphMeasure) => string | number> = {
  name: measure => measure.name,
  measureTypeName: measure => measure.type,
  measureGenre: measure => measure.measureGenre,
  localisationName: measure => (measure.localisation ?? '').split(', ').sort(SORT_COLLATOR.compare)[0] ?? '',
  unitName: measure => measure.unit,
  description: measure => measure.description ?? '',
};

@Component({
  selector: 'app-select-measures-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslocoModule,
    RadioButtonModule,
    CheckboxModule,
    TableModule,
    SelectModule,
    MultiSelectModule,
    InputTextModule,
    SkeletonModule,
    CesDialogFooterComponent,
    CesIconComponent,
    SuspendedMeasureIconComponent,
  ],
  templateUrl: './select-measures-dialog.component.html',
  styleUrl: './select-measures-dialog.component.scss',
})
export class SelectMeasuresDialogComponent {
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject<DynamicDialogConfig<SelectMeasuresDialogData>>(DynamicDialogConfig);
  private readonly measureDataService = inject(MeasureDataService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslocoService);
  private readonly measureTypeStore = inject(MeasureTypeStore);
  private readonly locationStore = inject(LocationStore);
  private readonly measureLocationStore = inject(MeasureLocationStore);
  private readonly unitStore = inject(UnitStore);
  private readonly toastService = inject(ToastService);
  protected readonly PAGINATION_CONSTANTS = PAGINATION_CONSTANTS;

  private readonly table = viewChild<Table>('measuresTable');

  protected readonly saving = signal(false);

  private readonly assetId = this.config.data?.assetId ?? 0;

  readonly measures = signal<GraphMeasure[]>([]);
  readonly totalRecords = signal<number>(0);
  readonly isLoading = signal<boolean>(false);
  readonly tableAriaLabel = this.translate.translate('common.dialogs.selectMeasures.tableAriaLabel');
  readonly multiselect = signal<boolean>(true);
  readonly selectedMeasure = signal<GraphMeasure | null>(null);

  readonly showSelectedOnly = signal<boolean>(false);
  readonly selectedCount = computed(() => this.selectedIds().size);
  private readonly selectedIds = signal<Set<number>>(new Set(this.config.data?.preselectedIds ?? []));
  private readonly allSelectedMeasures = signal<GraphMeasure[]>([]);
  private readonly filteredSelectedMeasures = signal<GraphMeasure[]>([]);

  readonly nomControl = new FormControl('', { nonNullable: true });
  readonly typeMesureControl = new FormControl<string[]>([], { nonNullable: true });
  readonly genreControl = new FormControl<number[]>([], { nonNullable: true });
  readonly localisationControl = new FormControl<(number | string)[]>([], { nonNullable: true });
  readonly uniteControl = new FormControl<string | null>(null);
  readonly descriptionControl = new FormControl('', { nonNullable: true });

  protected readonly sortableColumns = [
    { field: 'name', width: '18%', labelKey: 'common.dialogs.selectMeasures.columns.name' },
    { field: 'measureTypeName', width: '15%', labelKey: 'common.dialogs.selectMeasures.columns.measureType' },
    { field: 'measureGenre', width: '12%', labelKey: 'common.dialogs.selectMeasures.columns.genre' },
    { field: 'localisationName', width: '15%', labelKey: 'common.dialogs.selectMeasures.columns.location' },
    { field: 'unitName', width: '12%', labelKey: 'common.dialogs.selectMeasures.columns.unit' },
    { field: 'description', width: '28%', labelKey: 'common.dialogs.selectMeasures.columns.description' },
  ] as const;

  readonly typeMesureOptions = signal<FilterOption[]>([]);
  readonly genreOptions = signal<{ label: string; value: number }[]>([]);
  readonly localisationOptions = signal<SelectOption[]>([]);
  readonly uniteOptions = signal<FilterOption[]>([]);
  private filterOptionsLoaded = false;
  private readonly measureLocationsByMeasure = signal<Map<number, number[]>>(new Map());

  readonly someVisibleSelected = computed(() => {
    const visible = this.measures();
    return visible.length > 0 && visible.some(m => m.selected);
  });

  readonly allVisibleSelected = computed(() => {
    const visible = this.measures();
    return visible.length > 0 && visible.every(m => m.selected);
  });

  readonly displayData = computed<(GraphMeasure & { isPlaceholder?: boolean })[]>(() => {
    if (this.isLoading()) {
      return Array.from({ length: 10 }, (_, i) => ({
        id: -(i + 1),
        measureGenre: 0,
        code: '',
        name: '',
        unit: '',
        type: '',
        minTimestamp: undefined,
        maxTimestamp: undefined,
        isPlaceholder: true,
      }));
    }
    return this.measures();
  });

  private lastLazyLoadEvent: TableLazyLoadEvent | null = null;
  // Rapid header clicks (sort asc → desc) overlap requests; only the latest
  // response may drive the grid or the icons lie about the displayed order.
  private loadRequestId = 0;

  constructor() {
    this.multiselect.set(this.config.data?.multiselect ?? true);

    merge(
      this.nomControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()),
      this.descriptionControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()),
      this.typeMesureControl.valueChanges,
      this.genreControl.valueChanges,
      this.localisationControl.valueChanges,
      this.uniteControl.valueChanges,
    )
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.showSelectedOnly()) {
          this.applyClientSideFilters();
        } else if (this.lastLazyLoadEvent) {
          this.onLazyLoad({ ...this.lastLazyLoadEvent, first: 0 });
        }
      });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.lastLazyLoadEvent = event;

    if (this.showSelectedOnly()) {
      this.paginateClientSide(event);
      return;
    }

    const page = (event.first ?? 0) / (event.rows ?? PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE) + 1;
    const pageSize = event.rows ?? PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE;

    const filters: FilterParams[] = [];

    const nom = this.nomControl.value?.trim();
    if (nom) {
      filters.push({ field: 'name', value: nom, matchMode: 'contains' });
    }

    const types = this.typeMesureControl.value ?? [];
    if (types.length > 0) {
      filters.push(buildEmptyValueFilter('measureTypeName', types));
    }

    const genres = this.genreControl.value ?? [];
    if (genres.length > 0) {
      filters.push({ field: 'measureGenre', value: genres, matchMode: 'in' });
    }

    const localisations = this.localisationControl.value ?? [];
    if (localisations.length > 0) {
      filters.push({ field: 'localisationId', value: localisations, matchMode: 'in' });
    }

    const unite = this.uniteControl.value;
    if (unite) {
      filters.push({ field: 'unitName', value: unite, matchMode: 'equals' });
    }

    const description = this.descriptionControl.value?.trim();
    if (description) {
      filters.push({ field: 'description', value: description, matchMode: 'contains' });
    }

    const sorts = LazyLoadConverter.fromTableEvent(event).sorts ?? [];
    const params: LazyLoadParams & { assetId: number } = {
      assetId: this.assetId,
      pagination: { page, pageSize },
      filters,
      ...(sorts.length ? { sorts } : {}),
    };

    this.isLoading.set(true);
    const requestId = ++this.loadRequestId;

    this.measureDataService
      .getDetailedMeasuresPaginated(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          if (requestId !== this.loadRequestId) return;
          const currentSelectedIds = this.selectedIds();
          const locationsByMeasure = this.measureLocationsByMeasure();
          const enrichedMeasures = response.data.map(measure => ({
            ...measure,
            measureTypeName: measure.measureTypeName ?? '',
            unitName: measure.unitName ?? '',
            unitSymbol: measure.unitSymbol ?? '',
            localisationName: measure.localisationName ?? '',
            locationIds: locationsByMeasure.get(measure.id) ?? [],
            genreLabel: this.translate.translate(getGenreLabelKey(measure.type)),
            selected: currentSelectedIds.has(measure.id),
          }));

          const graphMeasures = enrichedMeasures.map(m => toGraphMeasure(m, m.selected));

          if (!this.multiselect() && this.selectedMeasure() === null) {
            this.selectedMeasure.set(graphMeasures.find(mes => currentSelectedIds.has(mes.id)) ?? null);
          }

          this.measures.set(graphMeasures);
          this.totalRecords.set(response.total);
          this.isLoading.set(false);

          if (!this.filterOptionsLoaded) {
            void this.loadFilterOptionsFromStores();
          }
        },
        error: () => {
          if (requestId !== this.loadRequestId) return;
          this.isLoading.set(false);
          this.measures.set([]);
          this.totalRecords.set(0);
          this.toastService.error('common.messages.loadFailed');
        },
      });
  }

  private async loadFilterOptionsFromStores(): Promise<void> {
    this.filterOptionsLoaded = true;

    const [measureTypes, allLocations, measureLocations, units] = await Promise.all([
      this.measureTypeStore.getByAssetId(this.assetId),
      this.locationStore.getByAssetId(this.assetId),
      this.measureLocationStore.getByAssetId(this.assetId),
      this.unitStore.getByAssetId(this.assetId),
    ]);

    const locationIdsWithMeasures = new Set(measureLocations.map((ml: MeasureLocation) => ml.locationId));
    const locationsWithMeasures = allLocations.filter((loc: Location) => locationIdsWithMeasures.has(loc.id));

    const locationsByMeasure = new Map<number, number[]>();
    for (const ml of measureLocations) {
      const existing = locationsByMeasure.get(ml.measureId);
      existing ? existing.push(ml.locationId) : locationsByMeasure.set(ml.measureId, [ml.locationId]);
    }
    this.measureLocationsByMeasure.set(locationsByMeasure);

    this.typeMesureOptions.set(
      buildFilterOptionsWithEmpty(
        measureTypes.map((mt: MeasureTypeEntity) => mt.name),
        this.translate.translate('common.dialogs.selectMeasures.filters.noType'),
      ),
    );

    const genreKeys = [MeasureType.MANUAL, MeasureType.AUTOMATIC, MeasureType.ANALYSIS_POST] as const;
    this.genreOptions.set(genreKeys.map(type => ({ label: this.translate.translate(getGenreLabelKey(type)), value: type })));

    this.localisationOptions.set(
      buildLocationFilterOptions(
        locationsWithMeasures.map((loc: Location) => ({ id: loc.id, name: loc.name })),
        this.translate.translate('common.dialogs.selectMeasures.filters.noLocation'),
      ),
    );

    this.uniteOptions.set(
      units
        .map((u: Unit) => ({ label: formatUnitLabel(u.name, u.symbol), value: u.name }))
        .sort((a: FilterOption, b: FilterOption) => a.label.localeCompare(b.label)),
    );
  }

  trackByMeasureId(_index: number, measure: GraphMeasure): number {
    return measure.id;
  }

  toggleMeasure(id: number, selected: boolean): void {
    this.updateSelectedId(id, selected);

    if (!this.showSelectedOnly()) {
      this.measures.update(measures => measures.map(m => (m.id === id ? { ...m, selected } : m)));
      return;
    }

    if (selected) return;

    this.allSelectedMeasures.update(measures => measures.filter(m => m.id !== id));
    this.filteredSelectedMeasures.update(measures => measures.filter(m => m.id !== id));
    this.measures.update(measures => measures.filter(m => m.id !== id));
    this.totalRecords.update(total => total - 1);

    if (this.selectedIds().size === 0) {
      this.exitSelectedOnlyMode();
    }
  }

  toggleAllVisible(selected: boolean): void {
    const visibleMeasures = this.measures();
    this.selectedIds.update(set => {
      const newSet = new Set(set);
      visibleMeasures.forEach(m => (selected ? newSet.add(m.id) : newSet.delete(m.id)));
      return newSet;
    });

    if (!this.showSelectedOnly() || selected) {
      this.measures.update(measures => measures.map(m => ({ ...m, selected })));
      return;
    }

    const visibleIds = new Set(visibleMeasures.map(m => m.id));
    this.allSelectedMeasures.update(all => all.filter(m => !visibleIds.has(m.id)));
    this.filteredSelectedMeasures.update(all => all.filter(m => !visibleIds.has(m.id)));
    this.applyClientSideFilters();

    if (this.selectedIds().size === 0) {
      this.exitSelectedOnlyMode();
    }
  }

  toggleShowSelectedOnly(checked: boolean): void {
    this.showSelectedOnly.set(checked);
    if (checked) {
      this.resetTablePagination();
      this.loadSelectedMeasures();
    } else if (this.lastLazyLoadEvent) {
      this.resetTablePagination();
      this.onLazyLoad({ ...this.lastLazyLoadEvent, first: 0 });
    }
  }

  cancel(): void {
    this.ref.close();
  }

  save(): void {
    if (this.saving()) return;
    if (!this.multiselect()) {
      const selected = this.selectedMeasure();
      const result: MeasureSelectionResult = {
        selectedMeasures: selected ? [selected] : [],
      };
      this.ref.close(result);
      return;
    }

    const ids = Array.from(this.selectedIds());
    if (ids.length === 0) {
      this.ref.close({ selectedMeasures: [], detailedMeasures: [] });
      return;
    }

    this.saving.set(true);
    this.measureDataService
      .getDetailedMeasuresByIds(this.assetId, ids)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ graphMeasures, detailedMeasures }) => this.ref.close({ selectedMeasures: graphMeasures, detailedMeasures }),
        error: () => {
          this.saving.set(false);
          this.toastService.error('common.messages.loadFailed');
        },
      });
  }

  private resetTablePagination(): void {
    const table = this.table();
    if (table) {
      table.first = 0;
    }
  }

  private updateSelectedId(id: number, selected: boolean): void {
    this.selectedIds.update(set => {
      const newSet = new Set(set);
      selected ? newSet.add(id) : newSet.delete(id);
      return newSet;
    });
  }

  private exitSelectedOnlyMode(): void {
    this.showSelectedOnly.set(false);
    this.resetTablePagination();
    if (this.lastLazyLoadEvent) {
      this.onLazyLoad({ ...this.lastLazyLoadEvent, first: 0 });
    }
  }

  private loadSelectedMeasures(): void {
    const ids = Array.from(this.selectedIds());
    if (ids.length === 0) {
      this.showSelectedOnly.set(false);
      return;
    }

    this.isLoading.set(true);
    this.measureDataService
      .getMeasuresByIds(this.assetId, ids)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: measures => {
          if (!this.showSelectedOnly()) {
            this.isLoading.set(false);
            return;
          }

          const returnedIds = new Set(measures.map(m => m.id));
          this.selectedIds.update(set => {
            const newSet = new Set<number>();
            for (const id of set) {
              if (returnedIds.has(id)) newSet.add(id);
            }
            return newSet;
          });

          const locationsByMeasure = this.measureLocationsByMeasure();
          const graphMeasures = measures.map(m => ({ ...m, selected: true, locationIds: locationsByMeasure.get(m.id) ?? [] }));
          this.allSelectedMeasures.set(graphMeasures);
          this.filteredSelectedMeasures.set(graphMeasures);
          this.applyClientSideFilters();
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.showSelectedOnly.set(false);
        },
      });
  }

  private paginateClientSide(event: TableLazyLoadEvent): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE;
    const sorted = this.sortClientSide(this.filteredSelectedMeasures(), event);

    this.measures.set(sorted.slice(first, first + rows));
  }

  private sortClientSide(measures: GraphMeasure[], event: TableLazyLoadEvent): GraphMeasure[] {
    const sort = LazyLoadConverter.fromTableEvent(event).sorts?.[0];
    const getValue = sort ? CLIENT_SORT_VALUES[sort.field] : undefined;
    if (!sort || !getValue) return measures;

    const direction = sort.order === 'desc' ? -1 : 1;
    return [...measures].sort((a, b) => {
      const left = getValue(a);
      const right = getValue(b);
      const comparison =
        typeof left === 'number' && typeof right === 'number' ? left - right : SORT_COLLATOR.compare(String(left), String(right));
      return comparison * direction;
    });
  }

  private applyClientSideFilters(): void {
    let filtered = [...this.allSelectedMeasures()];

    const nom = this.nomControl.value?.trim().toLowerCase();
    if (nom) {
      filtered = filtered.filter(m => m.name.toLowerCase().includes(nom));
    }

    const types = this.typeMesureControl.value ?? [];
    if (types.length > 0) {
      filtered = filtered.filter(m => matchesEmptyValueSelection(m.type, types));
    }

    const genres = this.genreControl.value ?? [];
    if (genres.length > 0) {
      filtered = filtered.filter(m => genres.includes(m.measureGenre));
    }

    const localisations = this.localisationControl.value ?? [];
    if (localisations.length > 0) {
      filtered = filtered.filter(m => matchesLocationFilter(m.locationIds ?? [], localisations));
    }

    const unite = this.uniteControl.value;
    if (unite) {
      filtered = filtered.filter(m => m.unit === unite);
    }

    const description = this.descriptionControl.value?.trim().toLowerCase();
    if (description) {
      filtered = filtered.filter(m => m.description?.toLowerCase().includes(description));
    }

    this.filteredSelectedMeasures.set(filtered);
    this.totalRecords.set(filtered.length);

    this.resetTablePagination();
    const baseEvent = this.lastLazyLoadEvent ?? { rows: PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE };
    this.paginateClientSide({ ...baseEvent, first: 0 });
  }
}
